import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, MapPin, ChevronLeft, ChevronRight, Briefcase } from "lucide-react";
import { JOB_LISTINGS } from "../data";

const ease = [0.16, 1, 0.3, 1];
const AUTO_ADVANCE_MS = 5000;
const PER_SLIDE_DESKTOP = 3;

function calcPerSlide(width) {
  if (width >= 1024) return 3;
  if (width >= 640) return 2;
  return 1;
}

export function FeaturedRoles() {
  const [page, setPage] = useState(0);
  const [paused, setPaused] = useState(false);
  const [perSlide, setPerSlide] = useState(PER_SLIDE_DESKTOP);

  useEffect(() => {
    const onResize = () => {
      setPerSlide(calcPerSlide(window.innerWidth));
    };
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const totalPages = Math.max(1, Math.ceil(JOB_LISTINGS.length / perSlide));
  const safePage = Math.min(page, totalPages - 1);

  const next = useCallback(() => setPage((p) => (p + 1) % totalPages), [totalPages]);
  const prev = useCallback(() => setPage((p) => (p - 1 + totalPages) % totalPages), [totalPages]);

  useEffect(() => {
    if (paused) return;
    const id = setInterval(next, AUTO_ADVANCE_MS);
    return () => clearInterval(id);
  }, [paused, next]);

  const start = safePage * perSlide;
  const visible = JOB_LISTINGS.slice(start, start + perSlide);

  return (
    <section className="section-y container-x" data-testid="featured-roles-section">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-10 gap-6">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#E9610E] font-semibold mb-3">
            Featured Open Roles
          </p>
          <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight text-[#0F1E3C] text-balance max-w-2xl">
            Live searches. Real hiring managers.
          </h2>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={prev}
            data-testid="roles-prev"
            aria-label="Previous roles"
            className="w-11 h-11 rounded-full bg-white border border-[#EAE3D2] flex items-center justify-center hover:bg-[#FFF4EA] hover:border-[#E9610E] transition-colors"
          >
            <ChevronLeft className="w-4 h-4 text-[#0F1E3C]" />
          </button>
          <button
            onClick={next}
            data-testid="roles-next"
            aria-label="Next roles"
            className="w-11 h-11 rounded-full bg-white border border-[#EAE3D2] flex items-center justify-center hover:bg-[#FFF4EA] hover:border-[#E9610E] transition-colors"
          >
            <ChevronRight className="w-4 h-4 text-[#0F1E3C]" />
          </button>
          <Link
            to="/careers"
            className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold text-[#E9610E] ml-2 hover:gap-3 transition-all"
          >
            View all jobs <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      <div
        className="relative"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={safePage}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -30 }}
            transition={{ duration: 0.5, ease }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
          >
            {visible.map((j, idx) => (
              <RoleCard
                key={j.id}
                id={j.id}
                title={j.title}
                location={j.location}
                type={j.type}
                industry={j.industry}
                posted={j.posted}
                delay={idx * 0.08}
              />
            ))}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Pagination dots */}
      <div className="mt-10 flex items-center justify-center gap-2">
        {Array.from({ length: totalPages }).map((_, i) => (
          <button
            key={`slide-${totalPages}-${i}`}
            onClick={() => setPage(i)}
            data-testid={`roles-dot-${i}`}
            aria-label={`Go to slide ${i + 1}`}
            className={`h-1.5 rounded-full transition-all ${
              i === safePage ? "w-8 bg-[#E9610E]" : "w-1.5 bg-[#EAE3D2] hover:bg-[#C9BFA8]"
            }`}
          />
        ))}
      </div>
    </section>
  );
}

function RoleCard({ id, title, location, type, industry, posted, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay, ease }}
      data-testid={`featured-role-${id}`}
    >
      <Link
        to="/careers"
        className="block card-soft rounded-2xl p-6 sm:p-7 h-full group transition-all duration-300"
      >
        <div className="flex items-start justify-between mb-5">
          <div className="w-11 h-11 rounded-xl bg-[#FFF4EA] text-[#E9610E] flex items-center justify-center">
            <Briefcase className="w-5 h-5" />
          </div>
          <span className="text-[11px] text-[#64748B]">{posted}</span>
        </div>

        <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#E9610E] mb-2">
          {industry}
        </p>
        <h3 className="font-display font-bold text-xl text-[#0F1E3C] leading-snug mb-3">
          {title}
        </h3>
        <div className="flex flex-wrap items-center gap-2 text-sm text-[#475569] mb-6">
          <span className="flex items-center gap-1">
            <MapPin className="w-3.5 h-3.5" /> {location}
          </span>
          <span>·</span>
          <span>{type}</span>
        </div>

        <span className="inline-flex items-center gap-1 text-sm font-semibold text-[#E9610E] group-hover:gap-2 transition-all">
          Apply now <ArrowUpRight className="w-3.5 h-3.5" />
        </span>
      </Link>
    </motion.div>
  );
}
